import React from 'react';

const QuantumPlanetBackground = ({ className = '' }) => {
  return (
    <div className={`absolute pointer-events-none ${className}`}>
      {/* Massive glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-full blur-[120px] scale-150"></div>
      
      <svg 
        viewBox="0 0 100 100" 
        className="w-full h-full"
        fill="none"
      >
        <defs>
          {/* Main sphere gradient */}
          <radialGradient id="bgSphereGradient" cx="30%" cy="30%">
            <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.6" />
            <stop offset="40%" stopColor="#0891b2" stopOpacity="0.4" />
            <stop offset="70%" stopColor="#1e3a5f" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#0a1628" stopOpacity="0.2" />
          </radialGradient>
          
          {/* Orbit gradients */}
          <linearGradient id="bgOrbitGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#a855f7" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#22d3ee" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#a855f7" stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id="bgOrbitGradient2" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.5" />
            <stop offset="50%" stopColor="#a855f7" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.5" />
          </linearGradient>
          <linearGradient id="bgOrbitGradient3" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.4" />
          </linearGradient>
          
          {/* Glow filter */}
          <filter id="bgGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          {/* Stronger glow for points */}
          <filter id="bgPointGlow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Outer atmosphere glow */}
        <circle 
          cx="50" 
          cy="50" 
          r="45" 
          fill="none"
          stroke="url(#bgOrbitGradient1)"
          strokeWidth="0.3"
          opacity="0.3"
        />
        
        {/* Central sphere */}
        <circle 
          cx="50" 
          cy="50" 
          r="28" 
          fill="url(#bgSphereGradient)"
          filter="url(#bgGlow)"
        />
        
        {/* Sphere surface details - grid lines */}
        <ellipse cx="50" cy="50" rx="28" ry="10" stroke="#22d3ee" strokeWidth="0.3" fill="none" opacity="0.3" />
        <ellipse cx="50" cy="50" rx="28" ry="18" stroke="#22d3ee" strokeWidth="0.2" fill="none" opacity="0.2" />
        <ellipse cx="50" cy="50" rx="10" ry="28" stroke="#22d3ee" strokeWidth="0.3" fill="none" opacity="0.3" />
        <ellipse cx="50" cy="50" rx="18" ry="28" stroke="#22d3ee" strokeWidth="0.2" fill="none" opacity="0.2" />
        
        {/* Orbital ring 1 - main */}
        <ellipse 
          cx="50" 
          cy="50" 
          rx="42" 
          ry="14" 
          stroke="url(#bgOrbitGradient1)" 
          strokeWidth="1.5"
          fill="none"
          transform="rotate(-25 50 50)"
          filter="url(#bgGlow)"
          className="animate-[spin_60s_linear_infinite]"
          style={{ transformOrigin: '50px 50px' }}
        />
        
        {/* Orbital ring 2 */}
        <ellipse 
          cx="50" 
          cy="50" 
          rx="38" 
          ry="12" 
          stroke="url(#bgOrbitGradient2)" 
          strokeWidth="1"
          fill="none"
          transform="rotate(35 50 50)"
          filter="url(#bgGlow)"
          className="animate-[spin_45s_linear_infinite_reverse]"
          style={{ transformOrigin: '50px 50px' }}
        />
        
        {/* Orbital ring 3 - subtle outer */}
        <ellipse 
          cx="50" 
          cy="50" 
          rx="46" 
          ry="8" 
          stroke="url(#bgOrbitGradient3)" 
          strokeWidth="0.5"
          fill="none"
          transform="rotate(10 50 50)"
          opacity="0.5"
        />
        
        {/* Glowing orbital points */}
        <circle cx="22" cy="38" r="2" fill="#22d3ee" filter="url(#bgPointGlow)" className="animate-pulse" />
        <circle cx="78" cy="58" r="1.5" fill="#a855f7" filter="url(#bgPointGlow)" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
        <circle cx="50" cy="15" r="1.2" fill="#ffffff" filter="url(#bgPointGlow)" className="animate-pulse" style={{ animationDelay: '1s' }} />
        <circle cx="35" cy="75" r="1" fill="#06b6d4" filter="url(#bgPointGlow)" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
        <circle cx="70" cy="30" r="1.3" fill="#8b5cf6" filter="url(#bgPointGlow)" className="animate-pulse" style={{ animationDelay: '0.7s' }} />
        
        {/* Highlight on sphere */}
        <circle cx="40" cy="40" r="8" fill="white" opacity="0.1" />
        <circle cx="38" cy="38" r="4" fill="white" opacity="0.15" />
      </svg>
    </div>
  );
};

export default QuantumPlanetBackground;
